import { ref, type Ref } from 'vue';
import * as signalR from '@microsoft/signalr';
import { apiClient, AUTH_TOKEN_KEY } from '@/api/client';

/**
 * Hub SignalR des complétions de tâches temps-réel (cf
 * Siclop.TheHo.Web.Hubs.TaskHub). Le backend pousse `TaskResolved` au seul
 * groupe player_{puId} quand une de SES tâches (build / recherche / production)
 * est résolue par le tick → le front merge le delta dans le store, sans reload.
 *
 * Pattern identique à useAllianceHub : après connexion, `RegisterPlayer(puId)`
 * rattache la connexion au groupe. Singleton global, init une fois après login.
 */

export interface TaskResolvedPayload {
  /** "Build" | "Research" | "ShipProduction". */
  taskType: string;
  planetId: number;
  /** Id de l'entité produite (PlanetBuilding pour un upgrade, etc.) ; peut être null. */
  entityId?: number | null;
}

type Unsub = () => void;
type Handler<T> = (payload: T) => void;
const resolvedHandlers = new Set<Handler<TaskResolvedPayload>>();

const connection = ref<signalR.HubConnection | null>(null);
const isConnected = ref(false);
const currentPuId = ref<number | null>(null);

export function useTaskHub() {
  async function getAccessToken(): Promise<string> {
    try {
      const base = apiClient.defaults.baseURL ?? '';
      const res = await fetch(`${base}/auth/token`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json() as { accessToken?: string; token?: string };
        if (data.accessToken) return data.accessToken;
        if (data.token) return data.token;
      }
    } catch { /* fallback localStorage */ }
    return localStorage.getItem(AUTH_TOKEN_KEY) ?? '';
  }

  function resolveHubUrl(): string {
    const base = apiClient.defaults.baseURL ?? '';
    const root = base.replace(/\/api\/?$/, '');
    return `${root}/hubs/tasks`;
  }

  async function init(playerUniverseId: number): Promise<void> {
    if (connection.value?.state === signalR.HubConnectionState.Connected
        && currentPuId.value === playerUniverseId) return;
    if (connection.value) await disconnect();
    currentPuId.value = playerUniverseId;

    const hub = new signalR.HubConnectionBuilder()
      .withUrl(resolveHubUrl(), { accessTokenFactory: () => getAccessToken() })
      .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 30_000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    hub.on('TaskResolved', (p: TaskResolvedPayload) => {
      for (const h of resolvedHandlers) try { h(p); } catch { /* swallow */ }
    });

    hub.onclose(() => { isConnected.value = false; });
    hub.onreconnected(async () => {
      isConnected.value = true;
      if (currentPuId.value !== null) {
        try { await hub.invoke('RegisterPlayer', currentPuId.value); } catch { /* ignore */ }
      }
    });

    try {
      await hub.start();
      connection.value = hub;
      isConnected.value = true;
      await hub.invoke('RegisterPlayer', playerUniverseId);
    } catch {
      isConnected.value = false;
    }
  }

  async function disconnect(): Promise<void> {
    if (!connection.value) return;
    try { await connection.value.stop(); } catch { /* ignore */ }
    connection.value = null;
    isConnected.value = false;
    currentPuId.value = null;
  }

  /** S'abonne aux complétions de tâches poussées. Retourne un unsubscribe. */
  function onTaskResolved(cb: Handler<TaskResolvedPayload>): Unsub {
    resolvedHandlers.add(cb);
    return () => resolvedHandlers.delete(cb) as unknown as void;
  }

  return {
    isConnected: isConnected as Readonly<Ref<boolean>>,
    init,
    disconnect,
    onTaskResolved,
  };
}
